import {
  Badge,
  Button,
  Card,
  Flex,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  InspectionsGet200ResponseRowsInner,
  InspectionsGetRequestSubInspectionTypeEnum,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerContextUnitsInner,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerRecordInnerInner,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
  ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSubInspectionsInner,
  SpcApiInspectionsInspectionIdSubmitPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type InspectionPlan,
  type InspectionUnit,
  type InspectionCriteria as InventoryInspectionCriteria,
} from "@/components/organism/form/inventoryTransaction/types";
import { InspectionMutations } from "@/api/inspect/useInspectQuery";
import { useInspection } from "@/components/organism/form/inventoryTransaction/InspectionContext";
import { InspectionExecution } from "@/components/organism/form/inventoryTransaction/InspectionExecution";
import { customNotification } from "@/utils/notificationShow";

type SubInspectionType = InspectionsGetRequestSubInspectionTypeEnum;

const SUB_STAGE_LABELS: Record<SubInspectionType, string> = {
  [InspectionsGetRequestSubInspectionTypeEnum.FIRST]: "초품",
  [InspectionsGetRequestSubInspectionTypeEnum.MIDDLE]: "중품",
  [InspectionsGetRequestSubInspectionTypeEnum.LAST]: "종품",
};

const SUB_STAGE_ORDER: SubInspectionType[] = [
  InspectionsGetRequestSubInspectionTypeEnum.FIRST,
  InspectionsGetRequestSubInspectionTypeEnum.MIDDLE,
  InspectionsGetRequestSubInspectionTypeEnum.LAST,
];

interface ProcessInspectionExecutionProps {
  inspection: InspectionsGet200ResponseRowsInner;
  onCancel?: () => void;
  onComplete?: () => void;
}

const buildDefaultCell = (
  criteria: ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
) => {
  switch (criteria.type) {
    case "boolean":
      return { value: false };
    case "number":
    case "select":
      return { value: null };
    case "description":
    default:
      return { value: null, isPassed: false };
  }
};

const describeCriteriaSpec = (
  criteria: ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner,
) => {
  if (criteria.type === "number") {
    const min = criteria.min ?? "-";
    const max = criteria.max ?? "-";
    return `${min} ~ ${max}`;
  }
  if (criteria.type === "select") {
    const options = Array.isArray(criteria.options) ? criteria.options : [];
    return options.filter(Boolean).join(", ") || "-";
  }
  if (criteria.type === "boolean") {
    return criteria.standard ? "체크시 합격" : "체크시 불합격";
  }
  if (criteria.type === "photo") {
    return "사진 업로드 필요";
  }
  return (criteria.standard as string) ?? "-";
};

const deriveSampleCountFromRecord = (
  record?:
    | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerRecordInnerInner[][]
    | null,
) => {
  if (!Array.isArray(record) || record.length === 0) {
    return 0;
  }
  return record.reduce((max, criteriaArr) => {
    if (!Array.isArray(criteriaArr)) return max;
    return Math.max(max, criteriaArr.length);
  }, 0);
};

const buildSampleLabel = (index: number) =>
  `SAMPLE-${String(index + 1).padStart(3, "0")}`;

const mapRecordCellToUnitValue = (
  criteria:
    | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSpecificationDataCriteriaInner
    | undefined,
  cell?: ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerRecordInnerInner,
) => {
  const type = criteria?.type ?? "description";
  const rawValue = cell?.value;
  switch (type) {
    case "boolean":
      return typeof rawValue === "boolean" ? rawValue : null;
    case "number":
      if (typeof rawValue === "number") return rawValue;
      if (typeof rawValue === "string" && rawValue.trim().length > 0) {
        const parsed = Number(rawValue);
        return Number.isNaN(parsed) ? null : parsed;
      }
      return null;
    case "select":
      return typeof rawValue === "string" ? rawValue : null;
    case "description": {
      const text =
        typeof rawValue === "string"
          ? rawValue
          : typeof rawValue === "number"
            ? String(rawValue)
            : "";
      return {
        text,
        isPassed: typeof cell?.isPassed === "boolean" ? cell.isPassed : false,
      };
    }
    case "photo":
      return undefined;
    default:
      return rawValue ?? null;
  }
};

export function ProcessInspectionExecution({
  inspection,
  onCancel,
  onComplete,
}: ProcessInspectionExecutionProps) {
  const {
    setCurrentPlan,
    setInspectionCriteria,
    setInspectionUnits,
    setCurrentUnitIndex,
  } = useInspection();
  const [subInspectionState, setSubInspectionState] = useState<
    ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerSubInspectionsInner[]
  >(inspection.subInspections ?? []);
  const [hasReportedComplete, setHasReportedComplete] = useState(false);

  useEffect(() => {
    setSubInspectionState(inspection.subInspections ?? []);
    setHasReportedComplete(false);
  }, [inspection]);

  const availableStages = useMemo(
    () =>
      SUB_STAGE_ORDER.filter((stage) =>
        subInspectionState.some((sub) => sub.subInspectionType === stage),
      ),
    [subInspectionState],
  );

  const nextStageToHandle = useMemo(() => {
    for (const stage of availableStages) {
      const target = subInspectionState.find(
        (sub) => sub.subInspectionType === stage,
      );
      if (target && !target.isDone) {
        return stage;
      }
    }
    return availableStages[0];
  }, [availableStages, subInspectionState]);

  const [activeStage, setActiveStage] = useState<SubInspectionType>(
    nextStageToHandle ?? InspectionsGetRequestSubInspectionTypeEnum.FIRST,
  );

  useEffect(() => {
    if (nextStageToHandle) {
      setActiveStage(nextStageToHandle);
    }
  }, [nextStageToHandle]);

  const activeSubInspection = useMemo(
    () =>
      subInspectionState.find((sub) => sub.subInspectionType === activeStage),
    [subInspectionState, activeStage],
  );

  const criteriaList =
    activeSubInspection?.specificationData?.criteria ??
    inspection.specificationData?.criteria ??
    [];

  const mappedInspectionCriteria = useMemo<InventoryInspectionCriteria[]>(
    () =>
      criteriaList.map((criteria, index) => ({
        id: criteria.name ? `${criteria.name}-${index}` : `criteria-${index}`,
        name: criteria.name ?? `검사 항목 ${index + 1}`,
        type: criteria.type,
        required: true,
        spec: describeCriteriaSpec(criteria),
        min:
          typeof criteria.min === "number"
            ? criteria.min
            : criteria.min !== undefined && criteria.min !== null
              ? Number(criteria.min)
              : undefined,
        max:
          typeof criteria.max === "number"
            ? criteria.max
            : criteria.max !== undefined && criteria.max !== null
              ? Number(criteria.max)
              : undefined,
        options: Array.isArray(criteria.options)
          ? (criteria.options.filter(Boolean) as string[])
          : undefined,
        photoRequired: criteria.type === "photo",
      })),
    [criteriaList],
  );

  const recordTemplate = useMemo(
    () =>
      criteriaList.map((criteria) =>
        Array.from({ length: criteria.sampleNum ?? 1 }).map(() =>
          buildDefaultCell(criteria),
        ),
      ),
    [criteriaList],
  );

  const buildRecord = useCallback(
    (
      record?:
        | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerRecordInnerInner[][]
        | null,
    ): ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerRecordInnerInner[][] =>
      recordTemplate.map((criteriaArr, criteriaIdx) =>
        criteriaArr.map((defaultCell, sampleIdx) => {
          const cell = record?.[criteriaIdx]?.[sampleIdx];
          if (cell && typeof cell === "object" && "value" in cell) {
            return { ...defaultCell, ...cell };
          }
          return { ...defaultCell };
        }),
      ),
    [recordTemplate],
  );

  const recordForm = useForm<SpcApiInspectionsInspectionIdSubmitPostRequest>({
    initialValues: {
      inspectionId: activeSubInspection?.id ?? inspection.id ?? 0,
      inspectionsInspectionIdSubmitPostRequest: {
        description: activeSubInspection?.description ?? inspection.description,
        record: buildRecord(
          activeSubInspection?.record ?? inspection.record ?? [[]],
        ),
      },
    },
  });

  useEffect(() => {
    recordForm.setValues({
      inspectionId: activeSubInspection?.id ?? inspection.id ?? 0,
      inspectionsInspectionIdSubmitPostRequest: {
        description: activeSubInspection?.description ?? inspection.description,
        record: buildRecord(
          activeSubInspection?.record ?? inspection.record ?? [[]],
        ),
      },
    });
  }, [
    activeSubInspection,
    buildRecord,
    inspection.description,
    inspection.id,
    inspection.record,
  ]);

  const orderedStageTypes = availableStages;

  const isStageDisabled = useCallback(
    (stage: SubInspectionType) => {
      const stageIndex = orderedStageTypes.indexOf(stage);
      if (stageIndex <= 0) {
        return false;
      }
      const prevStage = orderedStageTypes[stageIndex - 1];
      const prevData = subInspectionState.find(
        (sub) => sub.subInspectionType === prevStage,
      );
      return !(prevData?.isDone ?? false);
    },
    [orderedStageTypes, subInspectionState],
  );

  const fallbackSampleCount = useMemo(() => {
    const fromActive = deriveSampleCountFromRecord(activeSubInspection?.record);
    const fromInspection = deriveSampleCountFromRecord(inspection.record);
    const fromTemplate =
      recordTemplate.length === 0
        ? 0
        : recordTemplate.reduce((max, arr) => Math.max(max, arr.length), 0);
    return Math.max(fromActive, fromInspection, fromTemplate, 1);
  }, [activeSubInspection?.record, inspection.record, recordTemplate]);

  const resolvedSampleUnits = useMemo(() => {
    const contextUnits =
      (activeSubInspection?.context?.units as
        | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerContextUnitsInner[]
        | undefined) ??
      (inspection.context?.units as
        | ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerContextUnitsInner[]
        | undefined);
    const normalizedUnits: { label: string; unitIndex: number }[] =
      Array.isArray(contextUnits) && contextUnits.length > 0
        ? contextUnits.map((unit, index) => ({
            label: unit?.unitCode ?? buildSampleLabel(index),
            unitIndex:
              typeof unit?.unitIndex === "number"
                ? unit.unitIndex
                : typeof unit?.unitIndex === "string" && unit.unitIndex
                  ? Number(unit.unitIndex)
                  : index + 1,
          }))
        : [];
    if (normalizedUnits.length >= fallbackSampleCount) {
      return normalizedUnits;
    }
    const nextUnits = [...normalizedUnits];
    let unitIndexSeed =
      nextUnits.reduce((max, unit) => Math.max(max, unit.unitIndex), 0) ||
      nextUnits.length;
    for (let i = nextUnits.length; i < fallbackSampleCount; i += 1) {
      unitIndexSeed += 1;
      nextUnits.push({
        label: buildSampleLabel(i),
        unitIndex: unitIndexSeed,
      });
    }
    return nextUnits;
  }, [
    activeSubInspection?.context?.units,
    fallbackSampleCount,
    inspection.context?.units,
  ]);

  const resolvedSampleLabels = useMemo(
    () => resolvedSampleUnits.map((unit) => unit.label),
    [resolvedSampleUnits],
  );

  const planData = useMemo<InspectionPlan | null>(() => {
    if (!activeSubInspection) {
      return null;
    }
    const baseQuantity =
      typeof inspection.targetQuantity === "string"
        ? Number(inspection.targetQuantity)
        : typeof inspection.targetQuantity === "number"
          ? inspection.targetQuantity
          : 0;
    const inspectionTypeLabel = `${inspection.inspectionType ?? "공정"} - ${
      SUB_STAGE_LABELS[activeStage]
    }`;
    return {
      id: String(activeSubInspection.id ?? inspection.id ?? Date.now()),
      scope: "IN",
      documentNo:
        activeSubInspection.code ??
        inspection.code ??
        `INSP-${inspection.id ?? "0"}`,
      item:
        inspection.itemWithGrade?.name ??
        inspection.itemDate?.name ??
        inspection.itemCode ??
        "미지정",
      quantity: Number.isNaN(baseQuantity) ? 0 : baseQuantity,
      lot: inspection.lot?.name ?? "-",
      inspectionType: inspectionTypeLabel,
      version: inspection.specificationId
        ? `v${inspection.specificationId}`
        : "v1",
      samplingMethod:
        inspection.inspectionMethodType === "FULL" ? "ALL" : "SAMPLE",
      sampleSize: resolvedSampleLabels.length,
      sampleList: resolvedSampleLabels,
      status: activeSubInspection.isDone ? "SUBMITTED" : "IN_PROGRESS",
    };
  }, [
    activeStage,
    activeSubInspection,
    inspection.code,
    inspection.id,
    inspection.inspectionMethodType,
    inspection.inspectionType,
    inspection.itemCode,
    inspection.itemDate?.name,
    inspection.itemWithGrade?.name,
    inspection.lot?.name,
    inspection.specificationId,
    inspection.targetQuantity,
    resolvedSampleLabels,
  ]);

  useEffect(() => {
    if (!activeSubInspection || !planData) {
      return;
    }
    setCurrentPlan(planData);
  }, [activeSubInspection, planData, setCurrentPlan]);

  useEffect(() => {
    if (!activeSubInspection) {
      return;
    }
    setInspectionCriteria(mappedInspectionCriteria);
  }, [activeSubInspection, mappedInspectionCriteria, setInspectionCriteria]);

  useEffect(() => {
    if (!activeSubInspection) {
      return;
    }
    const sourceRecord =
      activeSubInspection.record ??
      inspection.record ??
      (recordTemplate as ProductionPlansGet200ResponseRowsInnerWorksInnerInspectionsInnerRecordInnerInner[][]);
    const nextUnits: InspectionUnit[] = resolvedSampleUnits.map(
      (unitInfo, index) => {
        const unitData: Record<string, unknown> = {};
        mappedInspectionCriteria.forEach((criteria, criteriaIdx) => {
          const cell = sourceRecord?.[criteriaIdx]?.[index];
          unitData[criteria.id] = mapRecordCellToUnitValue(
            criteriaList[criteriaIdx],
            cell,
          );
        });
        return {
          id: `${activeSubInspection.id}-${unitInfo.unitIndex}`,
          label: unitInfo.label,
          originalLabel: unitInfo.label,
          status: activeSubInspection.isDone
            ? "DONE"
            : index === 0
              ? "IN_PROGRESS"
              : "NOT_STARTED",
          data: unitData,
          hasDefect: false,
          unitIndex: unitInfo.unitIndex,
        };
      },
    );
    setInspectionUnits(nextUnits);
    setCurrentUnitIndex(0);
  }, [
    activeSubInspection,
    inspection.record,
    mappedInspectionCriteria,
    recordTemplate,
    resolvedSampleLabels,
    resolvedSampleUnits,
    setCurrentUnitIndex,
    setInspectionUnits,
    criteriaList,
  ]);

  const allStagesCompleted = useMemo(
    () =>
      orderedStageTypes.length > 0 &&
      orderedStageTypes.every((stage) => {
        const state = subInspectionState.find(
          (sub) => sub.subInspectionType === stage,
        );
        return state?.isDone;
      }),
    [orderedStageTypes, subInspectionState],
  );

  useEffect(() => {
    if (allStagesCompleted && !hasReportedComplete) {
      setHasReportedComplete(true);
      onComplete?.();
    }
  }, [allStagesCompleted, hasReportedComplete, onComplete]);

  const { mutate: submitInspection } = useMutation({
    mutationFn: (params: SpcApiInspectionsInspectionIdSubmitPostRequest) =>
      InspectionMutations.submit(params),
    onSuccess: () => {
      setSubInspectionState((prev) =>
        prev.map((sub) =>
          sub.id === activeSubInspection?.id
            ? {
                ...sub,
                record:
                  recordForm.values.inspectionsInspectionIdSubmitPostRequest
                    .record,
                description:
                  recordForm.values.inspectionsInspectionIdSubmitPostRequest
                    .description,
                isDone: true,
              }
            : sub,
        ),
      );
      customNotification.success({
        message: "검사 결과가 저장되었습니다.",
      });

      const currentIndex = orderedStageTypes.indexOf(activeStage);
      const nextStage = orderedStageTypes[currentIndex + 1];
      if (nextStage) {
        setActiveStage(nextStage);
      }
    },
    onError: () => {
      customNotification.error({
        message: "검사 결과 저장에 실패했습니다.",
      });
    },
  });

  const handleSubmit = () => {
    if (!activeSubInspection) return;
    submitInspection(recordForm.values);
  };

  const handleStageChange = (value: string | null) => {
    if (!value) return;
    const nextStage = value as SubInspectionType;
    if (isStageDisabled(nextStage)) return;
    setActiveStage(nextStage);
  };

  if (availableStages.length === 0 || !activeSubInspection) {
    return (
      <Stack spacing="md" w={"1300px"}>
        <Paper p="lg" withBorder>
          <Text size="sm" c="dimmed">
            현재 공정 검사에 사용할 세부 단계(sub inspection) 정보가 없습니다.
            검사 기준을 확인해 주세요.
          </Text>
        </Paper>
        <Flex justify="flex-end">
          <Button variant="outline" onClick={onCancel}>
            닫기
          </Button>
        </Flex>
      </Stack>
    );
  }

  return (
    <Stack spacing="lg" w={"1300px"}>
      <Flex justify="space-between" align="center">
        <Title order={4}>공정 검사 실행</Title>
        <Badge color={allStagesCompleted ? "green" : "blue"}>
          {allStagesCompleted
            ? "모든 검사가 완료되었습니다"
            : `${SUB_STAGE_LABELS[activeStage]} 진행 중`}
        </Badge>
      </Flex>
      <Paper p="lg" withBorder>
        <Stack spacing="lg">
          <Tabs value={activeStage} onTabChange={handleStageChange}>
            <Tabs.List>
              {availableStages.map((stage) => {
                const stageData = subInspectionState.find(
                  (sub) => sub.subInspectionType === stage,
                );
                const disabled = isStageDisabled(stage);
                const color = stageData?.isDone
                  ? "green"
                  : disabled
                    ? "gray"
                    : "blue";
                const statusLabel = stageData?.isDone
                  ? "완료"
                  : disabled
                    ? "대기"
                    : "진행";
                return (
                  <Tabs.Tab key={stage} value={stage} disabled={disabled}>
                    <Flex gap={6} align="center">
                      <Text size="sm">{SUB_STAGE_LABELS[stage]}</Text>
                      <Badge color={color} size="xs">
                        {statusLabel}
                      </Badge>
                    </Flex>
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>
          </Tabs>

          <Card withBorder padding="md">
            <Stack spacing="xs">
              <Text size="sm" fw={600}>
                {SUB_STAGE_LABELS[activeStage]} 검사 정보
              </Text>
              <Flex>
                <Text size="sm" c="dimmed">
                  목표 수량
                </Text>
                <Text size="sm" fw={500}>
                  {inspection.targetQuantity ?? "-"}
                </Text>
              </Flex>
              <Text size="sm" c="dimmed">
                각 단계는 순차적으로 진행되며, 완료된 단계만 다시 열 수
                있습니다.
              </Text>
            </Stack>
          </Card>
        </Stack>
      </Paper>

      {criteriaList.length === 0 ? (
        <Card withBorder padding="md">
          <Text size="sm" c="dimmed">
            검사 기준에 등록된 항목이 없습니다.
          </Text>
        </Card>
      ) : (
        <>
          <InspectionExecution recordForm={recordForm} showActionBar={false} />
          <Flex justify="space-between">
            <Button variant="outline" onClick={onCancel}>
              나가기
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isStageDisabled(activeStage)}
            >
              {SUB_STAGE_LABELS[activeStage]} 검사 저장
            </Button>
          </Flex>
        </>
      )}
    </Stack>
  );
}
